import { Dispatch } from 'react';
import { Action, Reducer } from 'redux';
import { PageResult } from '../../models/CoreModels';
import { CoreOneRosterUser, OneRosterClass, OneRosterClassUserFromEnrollment, OneRosterClassSource } from '../../models/CoreOneRosterModels';
import { OneRosterClassSearchPageRequest, SearchPageRequest } from '../../models/Requests';
import { CacheRequestState, ExtendedRequestState, RequestState } from '../../models/RequestState';
import InstanceClient from '../../services/InstanceClient';
import StatusCode from '../../util/StatusCode';
import store from '../store';


enum OneRosterClassActionType {
    ONEROSTER_CLASSES = 'ONEROSTER_CLASSES',
    ONEROSTER_CLASSES_FILTER = 'ONEROSTER_CLASSES_FILTER',
    ONEROSTER_CLASSES_UPDATE = 'ONEROSTER_CLASSES_UPDATE',
    ONEROSTER_CLASS = 'ONEROSTER_CLASS',
    ONEROSTER_CLASS_TEACHER = 'ONEROSTER_CLASS_TEACHER',
    ONEROSTER_CLASS_STUDENT = 'ONEROSTER_CLASS_STUDENT',
    ONEROSTER_CLASS_SOURCES = 'ONEROSTER_CLASS_SOURCES'
}

export type ClassTeachersRequestState = ExtendedRequestState<PageResult<OneRosterClassUserFromEnrollment>, { filter: Partial<SearchPageRequest> }>
export type ClassStudentsRequestState = ExtendedRequestState<PageResult<CoreOneRosterUser>, { filter: Partial<SearchPageRequest> }>

export interface OneRosterClassState {
    classes: CacheRequestState<PageResult<OneRosterClass>>
    filter: Partial<OneRosterClassSearchPageRequest>
    classInfo: RequestState<OneRosterClass>
    teachersByClass: ClassTeachersRequestState
    studentsByClass: ClassStudentsRequestState
    sources: RequestState<OneRosterClassSource[]>
}

interface OneRosterClass_ListClassesBase extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASSES,
    classes: CacheRequestState<PageResult<OneRosterClass>>
}

interface OneRosterClass_ListClassesUpdate extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASSES_UPDATE,
}

interface OneRosterClass_ListClassesFilter extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASSES_FILTER,
    filter: Partial<OneRosterClassSearchPageRequest>
}

interface OneRosterClass_GetClass extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASS,
    classInfo: RequestState<OneRosterClass>
}

interface OneRosterClass_Teachers extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASS_TEACHER,
    teachersByClass: ClassTeachersRequestState
}

interface OneRosterClass_Sources extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASS_SOURCES,
    sources: RequestState<OneRosterClassSource[]>
}

interface OneRosterClass_Students extends Action<OneRosterClassActionType> {
    type: OneRosterClassActionType.ONEROSTER_CLASS_STUDENT,
    studentsByClass: ClassStudentsRequestState
}

type OneRosterClass_ListClasses = OneRosterClass_ListClassesBase
    | OneRosterClass_ListClassesFilter
    | OneRosterClass_ListClassesUpdate;



export const getOneRosterClasses = (filter: Partial<OneRosterClassSearchPageRequest> = {}, update = false) => (dispatch: Dispatch<OneRosterClass_ListClasses>) => {
    dispatch({ type: OneRosterClassActionType.ONEROSTER_CLASSES_FILTER, filter: filter });

    if (update) {
        dispatch({ type: OneRosterClassActionType.ONEROSTER_CLASSES_UPDATE });
    }

    const url = '/api/core/oneroster/class';
    InstanceClient.get<PageResult<OneRosterClass>>(url, filter)
        .then(res => {
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASSES,
                classes: {
                    code: StatusCode.COMPLETE,
                    data: res.data
                }
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASSES,
                classes: { code: StatusCode.ERROR, error }
            })
        )
}

export const getOneRosterClass = (id: string) => (dispatch: Dispatch<OneRosterClass_GetClass>) => {
    const storeKey = store.getState().onerosterClass.classInfo.key;
    const key = id;
    if (key === storeKey) {
        return;
    }

    const url = '/api/core/oneroster/class/' + id;
    InstanceClient.get<OneRosterClass>(url)
        .then(res => {
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS,
                classInfo: { code: StatusCode.COMPLETE, data: res.data, key: key },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS,
                classInfo: { code: StatusCode.ERROR, error }
            })
        )
}

export const getOneRosterTeachersByClass = (id: string, filter: Partial<SearchPageRequest> = {}) => (dispatch: Dispatch<OneRosterClass_Teachers>) => {
    const storeKey = store.getState().onerosterClass.teachersByClass.key;
    const key = id + '-' + filter.onlyActive + '-' + filter.page;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: OneRosterClassActionType.ONEROSTER_CLASS_TEACHER,
        teachersByClass: { code: StatusCode.PENDING, filter },
    });

    const url = `/api/core/oneroster/class/${id}/teacher`;
    InstanceClient.get<PageResult<OneRosterClassUserFromEnrollment>>(url, filter)
        .then(res => {
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS_TEACHER,
                teachersByClass: { code: StatusCode.COMPLETE, data: res.data, filter, key },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS_TEACHER,
                teachersByClass: { code: StatusCode.ERROR, error, filter }
            })
        )
}

export const getOneRosterSourcesByClass = (id: string) => (dispatch: Dispatch<OneRosterClass_Sources>) => {
    const storeKey = store.getState().onerosterClass.sources.key;
    const key = id;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: OneRosterClassActionType.ONEROSTER_CLASS_SOURCES,
        sources: { code: StatusCode.PENDING },
    });

    const url = `/api/core/oneroster/class/${id}/source`;
    InstanceClient.get<OneRosterClassSource[]>(url)
        .then(res => {
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS_SOURCES,
                sources: { code: StatusCode.COMPLETE, data: res.data, key },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS_SOURCES,
                sources: { code: StatusCode.ERROR, error}
            })
        )
}

export const getOneRosterStudentsByClass = (id: string, filter: Partial<SearchPageRequest> = {}) => (dispatch: Dispatch<OneRosterClass_Students>) => {
    const storeKey = store.getState().onerosterClass.studentsByClass.key;
    const key = id + '-' + filter.onlyActive + '-' + filter.page;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: OneRosterClassActionType.ONEROSTER_CLASS_STUDENT,
        studentsByClass: { code: StatusCode.PENDING, filter },
    });

    const url = `/api/core/oneroster/class/${id}/student`;
    InstanceClient.get<PageResult<CoreOneRosterUser>>(url, filter)
        .then(res => {
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS_STUDENT,
                studentsByClass: { code: StatusCode.COMPLETE, data: res.data, filter, key },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterClassActionType.ONEROSTER_CLASS_STUDENT,
                studentsByClass: { code: StatusCode.ERROR, error, filter }
            })
        )
}

type OneRosterClassAction = OneRosterClass_ListClasses
    | OneRosterClass_GetClass
    | OneRosterClass_Students
    | OneRosterClass_Teachers
    | OneRosterClass_Sources;


const initialState: OneRosterClassState = {
    classInfo: { code: StatusCode.PENDING },
    teachersByClass: { code: StatusCode.PENDING, filter: {} }, 
    studentsByClass: { code: StatusCode.PENDING, filter: {} },
    sources: { code: StatusCode.PENDING},
    classes: { code: StatusCode.PENDING },
    filter: {},
}

export const reducer: Reducer<OneRosterClassState, OneRosterClassAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case OneRosterClassActionType.ONEROSTER_CLASSES:
            return {
                ...state,
                classes: action.classes,
            }
        case OneRosterClassActionType.ONEROSTER_CLASSES_FILTER:
            return {
                ...state,
                filter: action.filter
            }
        case OneRosterClassActionType.ONEROSTER_CLASSES_UPDATE:
            return {
                ...state,
                classes: { ...state.classes, code: StatusCode.PENDING }
            }
        case OneRosterClassActionType.ONEROSTER_CLASS:
            return {
                ...state,
                classInfo: action.classInfo,
            }
        case OneRosterClassActionType.ONEROSTER_CLASS_TEACHER:
            return {
                ...state,
                teachersByClass: action.teachersByClass,
            }
        case OneRosterClassActionType.ONEROSTER_CLASS_STUDENT:
            return {
                ...state,
                studentsByClass: action.studentsByClass,
            }
        case OneRosterClassActionType.ONEROSTER_CLASS_SOURCES:
            return {
                ...state,
                sources: action.sources,
            }
        default:
            return state;
    }
}

